import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useListParams } from "hooks/useListParams"
import {
    useAccountVendorPayments,
    useAccountVendorPayment,
    useDeleteAccountVendorPayment,
} from "hooks/useAccountVendorPayment"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"

import AccountVendorPaymentAddEdit from "./AccountVendorPaymentAddEdit"
import AccountVendorPaymentHeader from "./AccountVendorPaymentHeader"
import AccountVendorPaymentView from "./AccountVendorPaymentView"

const AccountVendorPaymentList = () => {
  document.title = "Account Vendor Payment List"

  const [modal, setModal] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [viewModal, setViewModal] = useState(false)
  const [selectedViewId, setSelectedViewId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } = useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const { data: responseData, isLoading, refetch, isRefetching } = useAccountVendorPayments(queryParams)
  const { data: viewResponse, isLoading: isViewLoading } = useAccountVendorPayment(selectedViewId, viewModal)
  const { mutateAsync: deleteItem, isPending: deleteLoading } = useDeleteAccountVendorPayment()

  const toggleViewModal = useCallback(() => {
    setViewModal(prev => !prev)
    if (viewModal) setSelectedViewId(null)
  }, [viewModal])

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) setSelectedData(null)
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleViewClick = item => {
    setSelectedViewId(item?.id || null)
    setViewModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Vendor payment deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch {
      toast.error("Failed to delete vendor payment.")
    }
  }

  const columns = useMemo(() => [
    { header: "ID", accessorKey: "id" },
    { header: "Vendor Bill", cell: ({ row }) => row.original.vendor_bill?.bill_no || row.original.vendorBill?.bill_no || "-" },
    { header: "Vendor", cell: ({ row }) => row.original.vendor_bill?.vendor?.name || row.original.vendorBill?.vendor?.name || "-" },
    { header: "Amount", cell: ({ row }) => <div className="d-flex flex-wrap gap-1 align-items-center"><Badge color="light" className="text-dark">{formatCurrency(row.original.amount || 0, "BDT")}</Badge>{row.original.is_reversed && <Badge color="secondary">Reversed</Badge>}</div> },
    { header: "Method", cell: ({ row }) => row.original.payment_method?.name || row.original.paymentMethod?.name || "-" },
    { header: "Date", cell: ({ row }) => row.original.payment_date ? formatReadableDate(row.original.payment_date) : "-" },
    {
      header: "Action",
      cell: ({ row }) => (
        <ul className="list-unstyled hstack gap-1 mb-0">
          <li>
            <Button color="primary" size="sm" onClick={() => handleViewClick(row.original)} id={`view-account-vendor-payment-${row.original.id}`}>
              <i className="mdi mdi-eye-outline" />
            </Button>
            <UncontrolledTooltip placement="top" target={`view-account-vendor-payment-${row.original.id}`}>View</UncontrolledTooltip>
          </li>
          <CanView access={[ACCESS.ACCOUNT_VENDOR_PAYMENT.DELETE]}>
            <li>
              <Button color="danger" size="sm" onClick={() => handleDeleteClick(row.original)} id={`delete-account-vendor-payment-${row.original.id}`}>
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip placement="top" target={`delete-account-vendor-payment-${row.original.id}`}>Delete</UncontrolledTooltip>
            </li>
          </CanView>
        </ul>
      ),
    },
  ], [])

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper title="Account Vendor Payment" breadcrumbItem="Account Vendor Payment List">
      <AccountVendorPaymentHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3" />
      <DeleteModal show={deleteModal} onDeleteClick={handleDelete} loading={deleteLoading} onCloseClick={() => setDeleteModal(false)} />

      <Row>
        <Col sm={4}><GlobalInputSearch value={params.search} onChange={setSearch} /></Col>
      </Row>

      <TableContainer columns={columns} data={data} isPagination={true} paginationInfo={paginationInfo} onPageChange={setPage} loading={isLoading || isRefetching} />

      <AccountVendorPaymentView
        modal={viewModal}
        toggleModal={toggleViewModal}
        payment={viewResponse?.data}
        loading={isViewLoading}
      />

      <AccountVendorPaymentAddEdit modal={modal} isEdit={false} editedData={selectedData} toggleModal={toggleModal} />
    </PageWrapper>
  )
}

export default AccountVendorPaymentList
